import { inject, NgModule } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterModule, RouterStateSnapshot, Routes, UrlTree } from '@angular/router';
import { LayoutComponent } from './layout/layout.component';
import { ErrorPageComponent } from './modules/error-page/error-page.component';
import { MyAccountService } from './core/my-account.service';
import { MsalGuard } from '@azure/msal-angular';
import { environment } from '@env/environment';
import { BrowserUtils } from '@azure/msal-browser';
import { AccessDeniedComponent } from './shared/components/errors/access-denied/access-denied.component';
import { BreadCrumbId, BreadcrumbItem } from './shared/directives/breadcrumbs';
import { MainInfo, MainInfoResolver } from './layout/main/main.info-resolver';
import { map, Observable, of, switchMap, tap } from 'rxjs';

const authenticatedGuardFn: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): boolean => {
  const router = inject(Router);
  const myAccountService = inject(MyAccountService);
  console.log("Making sure to activate this route only if authenticated and with access:", state.url);

  if (!myAccountService.isAuthenticated) {
    // Redirect to the login page and indicate guard cannot activate route
    console.log('Not authenticated, redirecting to login page');
    router.navigate(['login']);
    return false;
  }
  // Do not redirect and activate route
  console.log("Activating route for authenticated user with access:", state.url);
  return true;
};

const unauthenticatedGuardFn: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const router = inject(Router);
  const myAccountService = inject(MyAccountService);
  console.log("Making sure to activate this route only if unauthenticated:", state.url);

  if (myAccountService.isAuthenticated && myAccountService.currentOrganizationDirectory === undefined) {
    // Redirect to select organization page if already authenticated
    console.log('Redirect to select organization page');
    return router.createUrlTree(['organizations']);
  }
  if (myAccountService.isAuthenticated && myAccountService.currentOrganizationDirectory !== undefined) {
    // Redirect to the organization page if one has been selected
    console.log('Redirect to the organization page');
    return router.createUrlTree(['organizations', myAccountService.currentOrganizationDirectory]);
  }

  // Do not redirect and activate route
  console.log("Activating route for unauthenticated user:", state.url);
  return true;
};

const NoOrganizationSelectedAndNotClientOwnerAccountGuardFn: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean | UrlTree> => {
  const router = inject(Router);
  const myAccountService = inject(MyAccountService);
  console.log("Making sure to activate this route only if no organization is selected and account is not a client owner:", state.url);

  return of(true).pipe(
    map(decision => decision as boolean | UrlTree),
    map(decision => {
      // Redirect to the organization page if there is a selected directory
      if (decision === true && myAccountService.currentOrganizationDirectory !== undefined) {
        console.log('Redirect to organization page of the current directory');
        return router.createUrlTree(['organizations', myAccountService.currentOrganizationDirectory]);
      }

      return decision;
    }),
    switchMap(decision => {
      // Redirect to the organization page of the client owner
      if (decision === true) {
        return myAccountService.getOrganizationsCount().pipe(
          switchMap(count => {
            // Exactly one organization count
            if (count === 1) {
              return myAccountService.getOrganizations().pipe(
                map(organizations => {
                  // Exactly one organization → auto-select it
                  if (organizations.length === 1) { // Very likely the count will not change
                    console.log('Redirect to organization page of the single organization you have access to');
                    return router.createUrlTree(['organizations', organizations[0].organizationCode]);
                  } else {
                    console.warn("Somehow your account has access to", organizations.length, "organizations now");
                    return true;
                  }
                }),
              );
            } 

            return of(true);
          })
        );
      }

      return of(decision);
    }),
    tap(decision => {
      if (decision === true) console.log("Activating route:", state.url);
    }),
  );
};

const routes: Routes = [
  // Authenticated and with access to Survey-Instruments
  {
    path: 'login',
    canActivate: [unauthenticatedGuardFn],
    data: {
      layout: 'empty',
    },
    loadChildren: () => import('./modules/login/login.module').then(m => m.LoginModule),
  },
  {
    path: '',
    canActivate: environment.b2c !== undefined ? [MsalGuard, authenticatedGuardFn] : [authenticatedGuardFn],
    component: LayoutComponent,
    data: {
      layout: 'main',
    },
    resolve: {
      info: (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(MainInfoResolver).resolve(route, state),
    },
    children: [
      {
        path: '',
        redirectTo: 'organizations',
        pathMatch: 'full',
      },
      {
        path: 'organizations',
        data: {
          breadcrumbs: [
            'HOME', 
            { id: 'ORGANIZATIONS', hidden: false },
          ] as (BreadCrumbId | BreadcrumbItem)[],
        },
        canActivate: [NoOrganizationSelectedAndNotClientOwnerAccountGuardFn],
        loadChildren: () => import('./modules/home/home.module').then((m) => m.HomeModule),
      },
      {
        path: 'organizations/:organizationCode',
        canActivate: [],
        children: [
          // TODO - maybe have an actual organization home page that summarizes everything about the selected organization instead of redirecting to its Surveys Page by default
          {
            path: '',
            redirectTo: 'surveys',
            pathMatch: 'full',
          },
          {
            path: 'surveys',
            data: {
              breadcrumbs: [
                'HOME', 
                { id: 'ORGANIZATION_HOME', hidden: true },
                'ORGANIZATION_SURVEYS',
              ] as (BreadCrumbId | BreadcrumbItem)[],
            },
            loadChildren: () => import('./modules/questionnaires/questionnaires.module').then((m) => m.QuestionnairesModule),
          },
          {
            path: 'permissions',
            data: {
              breadcrumbs: [
                'HOME',
                { id: 'ORGANIZATION_HOME', hidden: true },
                'ORGANIZATION_PERMS',
              ] as (BreadCrumbId | BreadcrumbItem)[],
            },
            loadChildren: () => import('./modules/permission/permission.module').then((m) => m.PermissionModule),
          },
          {
            path: 'reports',
            loadChildren: () => import('./modules/reports/reports.module').then((m) => m.ReportsModule)
          },
        ],
      },
      {
        path: 'sample-reports/:instrumentType',
        data: {
          breadcrumbs: ['HOME', 'SAMPLE_REPORT'] as (BreadCrumbId | BreadcrumbItem)[],
        },
        loadChildren: () => import('./modules/sample-results/sample-results.module').then((m) => m.SampleResultsModule),
      },
    ],
  },
  { path: 'error', component: ErrorPageComponent },
  { path: 'access-denied', component: AccessDeniedComponent },
  // Wildcard route for a 404 page
  { path: '**', component: ErrorPageComponent },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, environment.b2c !== undefined ? {
    // Don't perform initial navigation in iframes or popups
    initialNavigation: !BrowserUtils.isInIframe() && !BrowserUtils.isInPopup() ? 'enabledNonBlocking' : 'disabled' // Set to enabledBlocking to use Angular Universal
  } : undefined)],
  exports: [RouterModule]
})
export class AppRoutingModule { }
